import * as React from 'react';

import { cn, TouchTextField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $deleteEntry,
  $itemsInterfaceDisabled,
  $itemsInterfaceFacilityDefaultUnit,
  $itemsInterfaceInventoryLocked,
  $itemsInterfacePricingLocked,
  $itemsInterfaceShowPricing,
  $itemsInterfaceWeightUnitMode,
  $updateLineItem,
} from '../_state';
import { NormalizedLineItem } from '../_types';
import {
  CameraButtonField,
  DeleteButtonField,
  EstimatedPriceField,
  GrossWeightField,
  InventoryItemField,
  NetWeightField,
  PricingUnitField,
  TareWeightField,
  UnitPriceField,
} from '../fields';
import {
  getItemRowGridCols,
  ITEM_ROW_ERROR_BORDER,
  ITEM_ROW_GROUP_FOCUS_WITHIN,
} from '../layout';
import { ItemRow } from '../layout/ItemRow';

import { $openVehicleDetails } from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { useUIPreferences } from '~/shared/hooks';

type ItemRowVehicleProps = {
  lineItem: NormalizedLineItem;
  autoFocus?: boolean;
  onFocusRow?: (id: string) => void;
  hasError?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ ITEM ROW VEHICLE ⚛️ ⚛️ ⚛️
//
export const ItemRowVehicle: React.FC<ItemRowVehicleProps> = ({
  lineItem,
  autoFocus = false,
  onFocusRow,
  hasError,
}) => {
  const t = useTranslations();

  // Refs
  const grossWeightRef = React.useRef<HTMLDivElement>(null);
  const inventorySelectRef = React.useRef<HTMLDivElement>(null);

  // Atoms
  const disabled = useAtomValue($itemsInterfaceDisabled);
  const inventoryLocked = useAtomValue($itemsInterfaceInventoryLocked);
  const pricingLocked = useAtomValue($itemsInterfacePricingLocked);
  const showPricing = useAtomValue($itemsInterfaceShowPricing);
  const weightUnit = useAtomValue($itemsInterfaceWeightUnitMode);
  const facilityDefaultUnit = useAtomValue($itemsInterfaceFacilityDefaultUnit);
  const onUpdateItem = useSetAtom($updateLineItem);
  const onDeleteItem = useSetAtom($deleteEntry);
  const openVehicleDetails = useSetAtom($openVehicleDetails);

  const { touchModeEnabled } = useUIPreferences();

  const inventoryFieldsDisabled = disabled || inventoryLocked;
  const pricingFieldsDisabled = disabled || pricingLocked;

  const baseFieldProps = {
    lineItem,
    onUpdateItem,
    onDeleteItem,
    weightUnit,
    facilityDefaultUnit,
  };

  const inventoryFieldProps = {
    ...baseFieldProps,
    disabled: inventoryFieldsDisabled,
  };

  const pricingFieldProps = {
    ...baseFieldProps,
    disabled: pricingFieldsDisabled,
  };

  const gridClassName = getItemRowGridCols(showPricing);

  // Callbacks
  const handleVinChange = React.useCallback(
    (value: string) => onUpdateItem(lineItem.id, { vehicleVin: value || null }),
    [lineItem.id, onUpdateItem],
  );

  const handleMakeChange = React.useCallback(
    (value: string) =>
      onUpdateItem(lineItem.id, { vehicleMake: value || null }),
    [lineItem.id, onUpdateItem],
  );

  const handleModelChange = React.useCallback(
    (value: string) =>
      onUpdateItem(lineItem.id, { vehicleModel: value || null }),
    [lineItem.id, onUpdateItem],
  );

  const handleYearChange = React.useCallback(
    (value: string) => {
      const parsed = parseInt(value, 10);
      onUpdateItem(lineItem.id, {
        vehicleYear: isNaN(parsed) ? null : parsed,
      });
    },
    [lineItem.id, onUpdateItem],
  );

  const handleDetailRowPress = React.useCallback(() => {
    openVehicleDetails({
      lineItemId: lineItem.id,
      itemId: lineItem.itemId,
      vin: lineItem.vehicleVin,
      make: lineItem.vehicleMake,
      model: lineItem.vehicleModel,
      year: lineItem.vehicleYear,
    });
  }, [
    lineItem.id,
    lineItem.itemId,
    lineItem.vehicleVin,
    lineItem.vehicleMake,
    lineItem.vehicleModel,
    lineItem.vehicleYear,
    openVehicleDetails,
  ]);

  const handleClickDetailRow = React.useCallback(
    (e: React.SyntheticEvent<HTMLDivElement>) => {
      if (touchModeEnabled) handleDetailRowPress();
      e.stopPropagation();
    },
    [touchModeEnabled, handleDetailRowPress],
  );

  // Effects
  /** Auto-focus the appropriate input when this row is newly added */
  React.useEffect(() => {
    if (!autoFocus) return;

    const targetRef = touchModeEnabled ? grossWeightRef : inventorySelectRef;
    const inputElement = targetRef?.current?.querySelector('input');
    if (inputElement) {
      setTimeout(() => {
        inputElement.focus();
        inputElement.select();
      }, 100);
    }
  }, [autoFocus, touchModeEnabled]);

  return (
    <div
      className={cn('@ItemRowVehicle group', hasError && ITEM_ROW_ERROR_BORDER)}
      onFocus={() => onFocusRow?.(lineItem.id)}
    >
      <ItemRow
        gridTemplate={gridClassName}
        className={cn('@ItemRowVehicle relative', ITEM_ROW_GROUP_FOCUS_WITHIN)}
      >
        <div ref={inventorySelectRef}>
          <InventoryItemField {...inventoryFieldProps} lineItem={lineItem} />
        </div>

        <div ref={grossWeightRef}>
          <GrossWeightField {...inventoryFieldProps} />
        </div>

        <TareWeightField {...inventoryFieldProps} />
        <NetWeightField {...inventoryFieldProps} />

        {showPricing && (
          <>
            <UnitPriceField {...pricingFieldProps} />
            <PricingUnitField {...pricingFieldProps} />
            <EstimatedPriceField {...pricingFieldProps} />
          </>
        )}

        <div />
        <CameraButtonField {...inventoryFieldProps} disabled />
        <DeleteButtonField {...inventoryFieldProps} />
      </ItemRow>

      <div
        className={cn(
          '@ItemRowVehicleDetails relative',
          'grid auto-rows-[2.75rem] gap-3 py-2 transition-all',
          'after:absolute after:right-0 after:bottom-0 after:left-[40px] after:h-px after:bg-neutral/20',
          gridClassName,
          touchModeEnabled && 'cursor-pointer',
        )}
        onClick={handleClickDetailRow}
      >
        <div className="pl-[40px]">
          <TouchTextField
            placeholder={t.scaleTicketPro.vehicleDetails.tabs.vin}
            value={lineItem.vehicleVin ?? ''}
            onChange={handleVinChange}
            isDisabled={inventoryFieldsDisabled}
          />
        </div>

        <TouchTextField
          placeholder={t.scaleTicketPro.vehicleDetails.tabs.make}
          value={lineItem.vehicleMake ?? ''}
          onChange={handleMakeChange}
          isDisabled={inventoryFieldsDisabled}
        />

        <TouchTextField
          placeholder={t.scaleTicketPro.vehicleDetails.tabs.model}
          value={lineItem.vehicleModel ?? ''}
          onChange={handleModelChange}
          isDisabled={inventoryFieldsDisabled}
        />

        <TouchTextField
          placeholder={t.scaleTicketPro.vehicleDetails.tabs.year}
          value={lineItem.vehicleYear?.toString() ?? ''}
          onChange={handleYearChange}
          isDisabled={inventoryFieldsDisabled}
        />

        {showPricing && (
          <>
            <div />
            <div />
            <div />
          </>
        )}

        <div />
        <div />
        <div />
      </div>
    </div>
  );
};
